/**
 * Shared between every screen that renders a `JobRef` — first written for `m-rpoint` (W144),
 * factored out here when `v-snapshots` (W145) needed the identical row rendering: same columns,
 * same null-configuration and trigger-badge rules (§9.h A1–A3).
 *
 * **`formatJobTime` is a fallback, not the normal path.** `MachineTime` "never appears on a
 * screen" (`LLD-XCC-076`) — the served `ScreenTime` twin (`.display`) is what every screen should
 * render, and every caller of this function is doing so only because the field in question has no
 * `ScreenTime` twin in the contract (`RestorePoint.configuration_snapshot`'s time,
 * `RestorePoint.expires_at`, `WorkspaceSummary.trial_ends_at`) — a reported gap, not a convention.
 */
export const TRIGGER_LABEL: Record<string, string> = { ON_DEMAND: 'On demand', SCHEDULED: 'Scheduled' }

/**
 * `content_bytes` (`SnapshotRepository`/`RestorePoint`) is a raw byte count with no server-side
 * `.display` twin — unlike `BackupSummary.storage.stored_bytes`, which is a `Quantity` and already
 * carries one. Binary units, one decimal place, matching `Prototype_v2.html`'s own figures (e.g.
 * `318.4 GB` for 341,920,415,744 bytes).
 */
export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  const units = ['KB', 'MB', 'GB', 'TB']
  let value = bytes / 1024
  let i = 0
  while (value >= 1024 && i < units.length - 1) {
    value /= 1024
    i++
  }
  return `${value.toFixed(1)} ${units[i]}`
}

/**
 * `JobStatus` → `chip` variant (W172), read directly off `Prototype_v2.html`'s own backup-jobs
 * history rows rather than assumed from the English words: `Running` is `st-pending`, `Success`
 * is `st-active`, `Partial success` is `solid-warn`, `Skipped` is `solid-grey`, `Failed` is
 * `solid-fail`. `PENDING` and `CANCELLED` have no direct row in the prototype — `PENDING` follows
 * `RUNNING` into `pending` (both "not yet resolved"); `CANCELLED` follows `SKIPPED` into `grey`
 * per this build's own already-written rule that a cancelled outcome is neutral, never a failure
 * (`REQ-UIX-070`/`REQ-UIX-074`, `v-jobsbackup`'s own doc comment).
 */
export const JOB_STATUS_CHIP: Record<string, 'pending' | 'active' | 'warning' | 'failure' | 'grey'> = {
  PENDING: 'pending',
  RUNNING: 'pending',
  SUCCEEDED: 'active',
  PARTIAL_SUCCESS: 'warning',
  FAILED: 'failure',
  SKIPPED: 'grey',
  CANCELLED: 'grey',
}

/**
 * `RestoreObjectResult` → `chip` variant (W172), read directly off `Prototype_v2.html`'s own
 * write-order/outcome tables: `Written` is `solid-ok`, `Mixed` is `solid-warn`, `Skipped` and
 * `Refused` are both `solid-grey`, `Failed` is `solid-fail`. `NEEDS_INPUT` has no direct row —
 * follows `MIXED` into `warning`, the same "needs a decision, not yet a failure" shape.
 */
export const RESTORE_RESULT_CHIP: Record<string, 'ok' | 'warning' | 'grey' | 'failure'> = {
  WRITTEN: 'ok',
  MIXED: 'warning',
  NEEDS_INPUT: 'warning',
  SKIPPED: 'grey',
  REFUSED: 'grey',
  FAILED: 'failure',
}

/**
 * `RestorePlan.pushes[].outcome` → `chip` variant, read directly off `Prototype_v2.html`'s own
 * push-plan table (`repo-wz-3`): `SINGLE_PUSH` is `solid-ok`; `CHUNKED` and `REFUSED_UNSPLITTABLE`
 * both render the plain `chip` class there (no `solid-*`), which is this component's `default`.
 */
export const PUSH_OUTCOME_CHIP: Record<string, 'ok' | 'default'> = {
  SINGLE_PUSH: 'ok',
  CHUNKED: 'default',
  REFUSED_UNSPLITTABLE: 'default',
}

export function formatJobTime(iso: string): string {
  const d = new Date(iso)
  const formatted = new Intl.DateTimeFormat('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false, timeZone: 'UTC' }).format(d)
  return `${formatted} UTC`
}
